/* el-cart-drawer — drawer behaviour, bound ONCE by delegation.
 *
 * Every listener here is attached to `document` or to the drawer SHELL, never to markup
 * inside [data-el-drawer-body]. The body is replaced wholesale by the Section Rendering
 * API after each cart mutation, so anything bound to its children would silently stop
 * working after the first quantity change — the classic version of this bug.
 *
 * Progressive enhancement is deliberate:
 *   - the cart control is <a href="/cart">, so no JS still reaches a real cart page;
 *   - add-to-cart forms are real <form action="/cart/add"> posts, intercepted here.
 */
(function () {
  var SECTION = 'el-cart-drawer';
  var drawer = null;
  var lastFocused = null;

  function el(sel, root) {
    return (root || document).querySelector(sel);
  }

  function getDrawer() {
    if (!drawer) drawer = el('[data-el-drawer]');
    return drawer;
  }

  function focusables() {
    var d = getDrawer();
    if (!d) return [];
    return Array.prototype.filter.call(
      d.querySelectorAll('a[href], button:not([tabindex="-1"]), select, input, [tabindex]:not([tabindex="-1"])'),
      function (node) {
        return node.offsetParent !== null && !node.disabled;
      }
    );
  }

  /* The page BEHIND the drawer. The Tab trap below stops keyboard focus from leaving, and
     it is the only thing that does: a screen reader's virtual cursor, and a pointer on the
     dimmed page, both walk straight through it. `inert` is what actually removes that
     content from focus, from the accessibility tree and from hit-testing.

     The drawer is rendered as a direct child of <body>, BEFORE the header group
     (layout/theme.liquid:410 / layout/el-lp.liquid:142), so none of these elements is an
     ancestor of the drawer — the contains() guard keeps that true if a layout ever moves it.
     `.el-lp-chrome` is the LP layout's header landmark; that layout has no section groups. */
  var INERT_SELECTOR = '#MainContent, .shopify-section-group-header-group, .shopify-section-group-footer-group, .el-lp-chrome, .skip-to-content-link';

  function pageInert(state) {
    var d = getDrawer();
    Array.prototype.forEach.call(document.querySelectorAll(INERT_SELECTOR), function (node) {
      if (d && node.contains(d)) return;
      if (state) node.setAttribute('inert', '');
      else node.removeAttribute('inert');
    });
  }

  /* Opening costs no layout inside the tap (tech refactor H9, 1 Oct 2026). Before, this function forced two
     full-page layouts — `void d.offsetWidth`, then focusables() reading offsetParent after the scroll-lock
     class and `inert` had invalidated the page: 40 ms of forced layout in the add-to-cart tap on a slow
     phone. Now: unhide only; two frames later set data-el-open so the panel slides from its off-screen start
     (a compositor transition); a task after that, lock scroll, make the page inert and focus the panel's
     close button (the first focusable, as before — not the backdrop button, which comes first in the DOM).
     A close() in between cancels through the ticket. */
  var openTicket = 0;
  var opening = false;

  function open() {
    var d = getDrawer();
    if (!d || opening || d.getAttribute('data-el-open') === 'true') return;
    lastFocused = document.activeElement;
    opening = true;
    var ticket = ++openTicket;
    d.hidden = false;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (ticket !== openTicket) return;
        d.setAttribute('data-el-open', 'true');
        opening = false;
        window.setTimeout(function () {
          if (ticket !== openTicket || d.getAttribute('data-el-open') !== 'true') return;
          document.documentElement.classList.add('el-scroll-locked');
          // before the focus move: blurs whatever behind the drawer had focus
          pageInert(true);
          var target = d.querySelector('[data-el-drawer-panel] [data-el-drawer-close]');
          if (target) target.focus({ preventScroll: true });
        }, 0);
      });
    });
  }

  function close() {
    var d = getDrawer();
    if (!d) return;
    if (opening) {
      // closed before the slide began: cancel the pending open entirely
      openTicket++;
      opening = false;
      d.hidden = true;
      return;
    }
    if (d.getAttribute('data-el-open') !== 'true') return;
    var ticket = ++openTicket;
    d.removeAttribute('data-el-open');
    document.documentElement.classList.remove('el-scroll-locked');
    window.setTimeout(function () {
      if (ticket !== openTicket) return;
      if (d.getAttribute('data-el-open') !== 'true') d.hidden = true;
    }, 320);
    // BEFORE the focus restore: focus() on a node inside an inert subtree is a no-op
    pageInert(false);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function busy(state) {
    var d = getDrawer();
    if (d) d.setAttribute('data-el-busy', state ? 'true' : 'false');
  }

  /* Replace only the body. The section response arrives wrapped in Shopify's
     #shopify-section-<id> element; take its inner HTML, not the wrapper. */
  function applySections(sections) {
    var d = getDrawer();
    if (!d || !sections) return;
    var html = sections[SECTION];
    if (!html) return;
    var host = el('[data-el-drawer-body]', d);
    if (!host) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var wrapper =
      parsed.getElementById('shopify-section-' + SECTION) ||
      parsed.querySelector('.shopify-section') ||
      parsed.body;
    /* Focus inside the body dies with the swap below and falls to <body>, behind a page that is inert: a keyboard
       or screen-reader shopper was lost after every add to cart and every quantity press (tech refactor C7,
       1 Oct 2026; live too). The same control in the new body takes it back — a quantity button by its line key
       and position, anything else by its first class and position — else the close button (the line is gone). */
    var focusSel = null;
    var focusIdx = 0;
    var active = document.activeElement;
    if (active && active !== host && host.contains(active)) {
      if (active.hasAttribute('data-el-qty')) {
        focusSel = '[data-el-qty][data-el-key="' + (active.getAttribute('data-el-key') || '').replace(/["\\]/g, '\\$&') + '"]';
      } else if (active.classList.length) {
        focusSel = active.tagName.toLowerCase() + '.' + active.classList[0];
      }
      if (focusSel) focusIdx = Array.prototype.indexOf.call(host.querySelectorAll(focusSel), active);
    }
    var hadFocus = !!(active && active !== host && host.contains(active));
    host.innerHTML = wrapper.innerHTML;
    if (hadFocus) {
      var again = focusSel ? host.querySelectorAll(focusSel)[focusIdx] : null;
      var target = again || host.querySelector('[data-el-drawer-close]');
      if (target) target.focus({ preventScroll: true });
    }
    // The rendered head carries the live count (data-el-drawer-count), so a mutation whose
    // JSON carries no item_count — every /cart/add.js — needs no follow-up /cart.js.
    var counted = host.querySelector('[data-el-drawer-count]');
    if (counted) {
      var n = parseInt(counted.getAttribute('data-el-drawer-count'), 10);
      if (!isNaN(n)) {
        setCount(n);
        return true;
      }
    }
    return false;
  }

  /* The pending body: shown the instant an add-to-cart is submitted, before the network
     answers. A cart that already has lines keeps them on screen, dimmed by the busy state;
     an EMPTY cart would otherwise sit on "Your cart is empty" while the add is in flight, so
     its empty state is swapped for a line-item skeleton (assets/el-cart-drawer.css). The
     rendered section then replaces the whole body. */
  var SKELETON =
    '<div class="el-drawer__skeleton" aria-hidden="true">' +
      '<div class="el-drawer__sk-tag"></div>' +
      '<div class="el-drawer__sk-row">' +
        '<div class="el-drawer__sk-thumb"></div>' +
        '<div class="el-drawer__sk-meta">' +
          '<div class="el-drawer__sk-line"></div>' +
          '<div class="el-drawer__sk-line el-drawer__sk-line--short"></div>' +
        '</div>' +
        '<div class="el-drawer__sk-side">' +
          '<div class="el-drawer__sk-price"></div>' +
          '<div class="el-drawer__sk-qty"></div>' +
        '</div>' +
      '</div>' +
      '<div class="el-drawer__sk-foot"><div class="el-drawer__sk-btn"></div></div>' +
    '</div>';

  function showPending() {
    var d = getDrawer();
    if (!d) return;
    var host = el('[data-el-drawer-body]', d);
    var empty = host ? host.querySelector('.el-drawer__empty') : null;
    if (empty) empty.outerHTML = SKELETON;
    busy(true);
    open();
  }

  /* The count text a screen reader hears, rebuilt from the two plural forms the header
     prints into data attributes (only Liquid can render a translation). Rendered with
     count 0, so the digit in "0 items" is the slot the live count goes into. */
  function countLabel(node, n) {
    var one = node.getAttribute('data-el-cart-count-one');
    var other = node.getAttribute('data-el-cart-count-other');
    if (n === 1 && one) return one;
    if (other) return other.replace('0', String(n));
    return String(n);
  }

  function setCount(n) {
    if (typeof n !== 'number') return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-el-cart-count]'), function (node) {
      node.textContent = n;
      node.hidden = n === 0;
    });
    // The badge is aria-hidden; without this the header keeps announcing the count the
    // page was loaded with, however many times the cart changes.
    Array.prototype.forEach.call(document.querySelectorAll('[data-el-cart-count-text]'), function (node) {
      node.textContent = countLabel(node, n);
    });
  }

  function refreshCount() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { setCount(cart.item_count); })
      .catch(function () {});
  }

  /* Re-render the drawer body from the server without changing the cart — used by Shopify's standard cart
     actions (tech refactor H9). A plain GET: the Section Rendering API must not be sent
     `Accept: application/json` (on /products/* that header returns product JSON instead). */
  function refresh() {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(base + '?sections=' + SECTION, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Drawer refresh failed: ' + r.status);
        return r.json();
      })
      .then(function (sections) {
        /* A failed section render can still answer 200 with this section null: that is a failure (the
           caller reloads), not a success that leaves stale lines and totals on screen (review). */
        if (!sections || typeof sections[SECTION] !== 'string' || !sections[SECTION]) {
          throw new Error('Drawer section missing from the response');
        }
        if (!applySections(sections)) return refreshCount();
      });
  }

  // Bare add — no section render, no busy toggle. Used as the first half of a
  // plan-preserving swap; the follow-up /cart/update.js does the repaint.
  function addLine(item) {
    busy(true);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || 'Cart add failed');
        return data;
      });
    });
  }

  function post(url, payload) {
    payload.sections = SECTION;
    payload.sections_url = window.location.pathname;
    busy(true);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'Cart request failed');
          return data;
        });
      })
      .then(function (data) {
        var counted = applySections(data.sections);
        if (typeof data.item_count === 'number') setCount(data.item_count);
        else if (!counted) refreshCount();
        busy(false);
        return data;
      })
      .catch(function (err) {
        busy(false);
        // A failed cart mutation must never leave a silently wrong drawer on screen.
        // Falling back to the real cart page is the honest recovery.
        console.error('[el-cart]', err);
        window.location.href = '/cart';
      });
  }

  document.addEventListener('click', function (event) {
    var openBtn = event.target.closest('[data-el-cart-open]');
    if (openBtn) {
      event.preventDefault();
      open();
      return;
    }

    if (event.target.closest('[data-el-drawer-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var qty = event.target.closest('[data-el-qty]');
    if (qty) {
      event.preventDefault();
      post('/cart/change.js', {
        id: qty.getAttribute('data-el-key'),
        quantity: parseInt(qty.getAttribute('data-el-to'), 10),
      });
      return;
    }

    var swap = event.target.closest('[data-el-swap]');
    if (swap) {
      event.preventDefault();
      // Carry the existing quantity across the swap. Hardcoding 1 here meant a customer
      // holding three packs lost two of them by accepting an upsell.
      var keep = parseInt(swap.getAttribute('data-el-keep'), 10);
      if (!keep || keep < 1) keep = 1;
      var plan = swap.getAttribute('data-el-plan');
      var fromKey = swap.getAttribute('data-el-from-key');

      if (plan && fromKey) {
        // Subscription line. /cart/update.js is keyed by VARIANT and carries no selling
        // plan, so swapping that way silently converts a subscription into a one-time
        // order at a higher price. Add the new line WITH the plan, then zero the old line
        // by its key. Only the second call needs the section render.
        addLine({ id: parseInt(swap.getAttribute('data-el-to-id'), 10), quantity: keep, selling_plan: parseInt(plan, 10) })
          .then(function () {
            var updates = {};
            updates[fromKey] = 0;
            return post('/cart/update.js', { updates: updates });
          })
          .catch(function () { busy(false); });
      } else {
        var updates = {};
        updates[swap.getAttribute('data-el-from')] = 0;
        updates[swap.getAttribute('data-el-to-id')] = keep;
        post('/cart/update.js', { updates: updates });
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    var d = getDrawer();
    if (!d || d.getAttribute('data-el-open') !== 'true') return;
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Intercept every add-to-cart form on the page. Doing it by delegation means the
     PDP buybox and the home offer block need no changes at all — which matters,
     because those files belong to another build lane. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    event.preventDefault();

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

    var body = new FormData(form);
    body.append('sections', SECTION);
    body.append('sections_url', window.location.pathname);

    /* OPEN ON THE CLICK (2026-09-05, Viktor: "clicking on Add to cart button is incredibly
       slow to open the cart"). Measured on the live PDP before this change: the panel opened
       1,281-1,387ms after the click — /cart/add.js with the section render (~0.5s warm, ~1s
       on a brand-new cart) and THEN a serial /cart.js (~0.34s) for the badge count, and only
       then open() and its 0.3s slide. Now the drawer is moving on the same frame as the
       click, the count rides the rendered section, and the one request left is the add. */
    showPending();

    fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'Add to cart failed');
          return data;
        });
      })
      .then(function (data) {
        var counted = applySections(data.sections);
        // The add response carries only the added line on this storefront; the count comes
        // off the rendered head. /cart.js is the fallback for a section that failed to render.
        if (typeof data.item_count === 'number') {
          setCount(data.item_count);
          return null;
        }
        return counted ? null : refreshCount();
      })
      .then(function () {
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
        busy(false);
      })
      .catch(function (err) {
        console.error('[el-cart]', err);
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
        busy(false);
        close();
        // let the browser do the real, non-JS submit so the customer is never stuck
        form.submit();
      });
  });

  /* Shopify's standard cart actions (Shopify.actions). Dawn's override pointed them at a <cart-drawer> that never
     renders here, so outside cart updates neither opened nor refreshed this drawer (tech refactor H9). openCart
     opens ours; updateCart refreshes ours WITHOUT opening it — an outside update must not pop the drawer — and
     keeps Dawn's recovery (reload) if the refresh fails. Other actions keep Shopify's defaults. */
  function initStandardActions() {
    var actions = window.Shopify && window.Shopify.actions;
    if (!actions) return;
    if (actions.openCart && actions.openCart.configure) {
      actions.openCart.configure({
        handler: function (defaultHandler) {
          if (!getDrawer()) return defaultHandler();
          open();
          return Promise.resolve();
        },
      });
    }
    if (actions.updateCart && actions.updateCart.configure) {
      actions.updateCart.configure({
        eventTarget: function () { return document; },
        handler: function (defaultHandler) {
          return Promise.resolve(defaultHandler()).then(function (result) {
            return refresh().then(
              function () { return result; },
              function (err) {
                console.error('[el-cart] standard-actions refresh failed; reloading.', err);
                window.location.reload();
                return result;
              }
            );
          });
        },
      });
    }
  }

  window.ElCartDrawer = { open: open, close: close, refresh: refresh };
  if (window.Shopify && window.Shopify.actions) initStandardActions();
  else document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
})();
